import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatIconModule } from "@angular/material/icon";
import {
  ButtonGroupItem,
  OmniarkUiModule,
  PaginationComponent,
  SearchComponent,
  StatusComponent,
} from "@corp-ais/omniark-ui";
import { finalize } from "rxjs";
import { EnrichmentListPriceItem } from "@app/shared/models/enrichment-detail.model";
import { OmniarkDrawerService } from "@app/shared/services/drawer.service";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import {
  PriceStatusChip,
  priceStatusChip,
} from "../../../../utils/price-entry.util";

type PackageDrawerView = "List" | "Grid";

const PACKAGE_PAGE_SIZES = [10, 20, 50];
const PACKAGE_SEARCH_MIN_LENGTH = 3;
const PACKAGE_SEARCH_DEBOUNCE_MS = 2_000;
/** An upsell target is a one-off or recurring package; usage, billing and
 *  nodisplay prices never qualify. */
const PACKAGE_PRICE_TYPES = "oneTime,recurring";

@Component({
  selector: "app-pricing-package-drawer",
  imports: [
    CommonModule,
    MatIconModule,
    OmniarkUiModule,
    SearchComponent,
    PaginationComponent,
    StatusComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./pricing-package-drawer.html",
  styleUrl: "./pricing-package-drawer.scss",
})
export class PricingPackageDrawer implements OnInit {
  private readonly enrichmentService = inject(EnrichmentService);
  private readonly drawerService = inject(OmniarkDrawerService);
  private readonly destroyRef = inject(DestroyRef);
  private searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private requestSequence = 0;

  readonly view = signal<PackageDrawerView>("List");

  readonly viewOptions: ButtonGroupItem[] = [
    { value: "List", label: "List", icon: "table_rows" },
    { value: "Grid", label: "Grid", icon: "grid_view" },
  ];
  readonly packages = signal<readonly EnrichmentListPriceItem[]>([]);
  readonly selectedPackage = signal<EnrichmentListPriceItem | null>(null);
  readonly packageSelected = output<EnrichmentListPriceItem>();
  readonly searchTerm = signal("");
  readonly currentPage = signal(1);
  readonly pageSize = signal(PACKAGE_PAGE_SIZES[0]);
  readonly totalItems = signal(0);
  readonly loading = signal(false);
  readonly loadError = signal("");

  readonly paginationConfig = computed(() => ({
    currentPage: this.currentPage(),
    totalItems: this.totalItems(),
    pageSize: this.pageSize(),
    pageSizeOptions: PACKAGE_PAGE_SIZES,
    showPageSizeSelector: true,
    showFirstLast: true,
  }));

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.clearSearchDebounce());
    this.loadPackages();
  }

  /** `enr-button-group` emits a string; ignore values outside this drawer's
   *  supported representations. */
  setView(value: string): void {
    if (value !== "List" && value !== "Grid") return;
    this.view.set(value);
  }

  onSearch(value: string): void {
    const query = value.trim();
    this.searchTerm.set(query);
    this.clearSearchDebounce();

    // An empty query restores the unfiltered result immediately. Non-empty
    // searches wait for three characters so short, accidental terms do not
    // repeatedly hit the paginated API.
    if (!query) {
      this.currentPage.set(1);
      this.loadPackages();
      return;
    }
    if (query.length < PACKAGE_SEARCH_MIN_LENGTH) return;

    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage.set(1);
      this.loadPackages();
    }, PACKAGE_SEARCH_DEBOUNCE_MS);
  }

  clearSearch(): void {
    this.clearSearchDebounce();
    this.searchTerm.set("");
    this.currentPage.set(1);
    this.loadPackages();
  }

  goToPage(page: number): void {
    if (page < 1 || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.loadPackages();
  }

  setPageSize(pageSize: number): void {
    if (!PACKAGE_PAGE_SIZES.includes(pageSize)) return;
    this.pageSize.set(pageSize);
    this.currentPage.set(1);
    this.loadPackages();
  }

  /** Only a price already live on the channel can be offered as the upsell
   *  target. The row's status is the channel overlay's, or the master POP's
   *  when the channel has no overlay yet. */
  isSelectable(item: EnrichmentListPriceItem): boolean {
    return item.lifecycleStatus?.trim().toLowerCase() === "launched";
  }

  statusChip(item: EnrichmentListPriceItem): PriceStatusChip | null {
    return priceStatusChip(item.lifecycleStatus);
  }

  selectPackage(item: EnrichmentListPriceItem): void {
    if (!this.isSelectable(item)) return;
    this.selectedPackage.set(item);
  }

  isSelected(id: string): boolean {
    return this.selectedPackage()?.id === id;
  }

  saveSelection(): void {
    const selectedPackage = this.selectedPackage();
    if (!selectedPackage) return;
    this.packageSelected.emit(selectedPackage);
    this.close();
  }

  close(): void {
    this.drawerService.closeAll();
  }

  trackPackage(_index: number, item: EnrichmentListPriceItem): string {
    return item.id;
  }

  priceLabel(item: EnrichmentListPriceItem): string {
    const value = item.price?.value;
    if (value === undefined || value === null) return "-";
    const unit = item.price?.unit?.trim().toLowerCase();
    if (unit === "baht" || unit === "thb") return `${value.toLocaleString()} ฿`;
    return unit ? `${value.toLocaleString()} ${unit}` : value.toLocaleString();
  }

  priceTypeLabel(item: EnrichmentListPriceItem): string {
    const priceType = item.priceType?.trim();
    return priceType
      ? priceType.replace(/^./, (char) => char.toUpperCase())
      : "-";
  }

  validForLabel(item: EnrichmentListPriceItem): string | null {
    const start = this.formatDate(item.validFor?.startDateTime);
    const end = this.formatDate(item.validFor?.endDateTime);
    if (start && end) return `${start} – ${end}`;
    return start ?? end;
  }

  packageMeta(item: EnrichmentListPriceItem): string {
    return [item.offering?.productType, item.offering?.offerClassificationName]
      .filter((value): value is string => Boolean(value))
      .join(" · ");
  }

  private loadPackages(): void {
    const requestSequence = ++this.requestSequence;
    this.loading.set(true);
    this.loadError.set("");
    this.enrichmentService
      .listEnrichmentPrice(
        {
          page: this.currentPage(),
          pageSize: this.pageSize(),
          q: this.searchTerm() || undefined,
          priceType: PACKAGE_PRICE_TYPES,
        },
        { channelTransport: "header" },
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (requestSequence === this.requestSequence) {
            this.loading.set(false);
          }
        }),
      )
      .subscribe({
        next: (response) => {
          if (requestSequence !== this.requestSequence) return;
          const packages = response.data ?? [];
          this.packages.set(packages);
          this.totalItems.set(
            response.pagination?.totalItems ?? packages.length,
          );
        },
        error: () => {
          if (requestSequence !== this.requestSequence) return;
          this.packages.set([]);
          this.totalItems.set(0);
          this.loadError.set("Unable to load package list. Please try again.");
        },
      });
  }

  private clearSearchDebounce(): void {
    if (!this.searchDebounceTimer) return;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = undefined;
  }

  private formatDate(value: string | undefined): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  }
}
